import React, { useState, useRef, useEffect } from 'react';

import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useImageAltText } from '@/hooks/useImageAltText';
import {
  buildCloudflareCDNUrl,
  isCloudflareCDNUrl,
  extractOriginalUrlFromCDN,
} from '@/utils/supabase/imageTransforms';

interface SEOImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif';
  useCDN?: boolean;
}

// Helper function to create CDN options
const createCDNOptions = (
  width?: number,
  height?: number,
  quality?: number,
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif'
) => ({
  ...(width && { width }),
  ...(height && { height }),
  ...(quality && { quality }),
  ...(format && { format }),
});

// Helper function to get optimized source
const getOptimizedSrc = (
  src: string,
  useCDN: boolean,
  width?: number,
  height?: number,
  quality?: number,
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif'
) => {
  if (!useCDN || !src || isCloudflareCDNUrl(src)) {
    return src;
  }
  return buildCloudflareCDNUrl(src, createCDNOptions(width, height, quality, format));
};

/**
 * SEO-optimized image component that automatically fetches alt text from image_seo_metadata
 * and uses Cloudflare CDN for image optimization
 * Priority: Database fetch > Fallback alt
 */
export const SEOImage = ({
  src,
  alt = '',
  width,
  height,
  quality,
  format,
  useCDN = true,
  onError,
  ...props
}: SEOImageProps) => {
  // Fetch SEO alt text from database with fallback
  const seoAlt = useImageAltText(src, alt);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const optimizedSrc = getOptimizedSrc(src, useCDN, width, height, quality, format);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      // If we haven't tried fallback yet and this is a CDN URL
      if (!hasError && isCloudflareCDNUrl(target.src)) {
        const originalUrl = extractOriginalUrlFromCDN(target.src);
        if (originalUrl) {
          // Cloudflare CDN transformation failed (9422), falling back to original image
          setCurrentSrc(originalUrl);
          setHasError(true);
          return;
        }
      }

      // Call custom onError handler if provided
      if (onError) {
        onError(event as unknown as React.SyntheticEvent<HTMLImageElement>);
      }
    };

    img.addEventListener('error', handleError);
    return () => img.removeEventListener('error', handleError);
  }, [hasError, onError]);

  return (
    <img
      ref={imgRef}
      src={currentSrc ?? optimizedSrc}
      alt={seoAlt}
      width={width}
      height={height}
      {...props}
    />
  );
};

const SEOImageWithMemoryLeakMonitor = withMemoryLeakMonitor(SEOImage, {
  componentName: 'SEOImage',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 20,
  frequencyThreshold: 3,
});

export default SEOImageWithMemoryLeakMonitor;
